import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import { contactAPI } from '../services/api';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
  repliedAt?: string;
  reply?: string;
  attachment_name?: string;
  attachment_mime?: string;
}

const Contact: React.FC = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'read' | 'replied'>('all');
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [overallStats, setOverallStats] = useState({
    total: 0,
    byStatus: { new: 0, read: 0, replied: 0 }
  });

  const loadOverallStats = async () => {
    try {
      const response = await contactAPI.getStats();
      if (response.success) {
        setOverallStats({
          total: response.data.total || 0,
          byStatus: {
            new: response.data.byStatus?.new || 0,
            read: response.data.byStatus?.read || 0,
            replied: response.data.byStatus?.replied || 0
          }
        });
      }
    } catch (err: any) {
      console.error('Error loading contact stats:', err);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: 1,
        limit: 100 // Load more messages for the email-like view
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await contactAPI.getMessages(params);

      if (response.success) {
        setMessages(response.data.messages || []);
        // Keep selected message updated
        if (selectedMessage) {
          const updatedMessage = response.data.messages.find((m: ContactMessage) => m.id === selectedMessage.id);
          if (updatedMessage) {
            setSelectedMessage(updatedMessage);
          }
        }
      } else {
        setError(response.error || 'Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const selectMessage = async (message: ContactMessage) => {
    try {
      // Fetch full message details (this will mark it as read if it's new)
      const response = await contactAPI.getMessage(message.id);
      if (response.success) {
        setSelectedMessage(response.data);
        setShowMobileDetails(true); // Show details on mobile
        // Update the message in the list
        setMessages(prev => prev.map(m =>
          m.id === message.id ? { ...m, status: response.data.status } : m
        ));
      }
    } catch (error) {
      console.error('Error selecting message:', error);
    }
  };

  const backToList = () => {
    setShowMobileDetails(false);
    setSelectedMessage(null);
    setShowReplyForm(false);
    setReplyText('');
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    try {
      setIsReplying(true);
      const response = await contactAPI.replyToMessage(selectedMessage.id, replyText.trim());

      if (response.success) {
        setSelectedMessage(response.data);
        setMessages(prev => prev.map(m =>
          m.id === selectedMessage.id ? response.data : m
        ));
        setReplyText('');
        setShowReplyForm(false);
      } else {
        setError(response.error || 'Failed to save note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      setError('Failed to save note');
    } finally {
      setIsReplying(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const response = await contactAPI.deleteMessage(messageId);
      if (response.success) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        if (selectedMessage?.id === messageId) {
          setSelectedMessage(null);
        }
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const handleDownloadAttachment = async (e: React.MouseEvent, messageId: string, fileName: string) => {
    e.stopPropagation();
    try {
      // We need to fetch with auth token
      const response = await fetch(contactAPI.getAttachmentUrl(messageId).split('?')[0], {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('shelter-token')}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Nu s-a putut descărca atașamentul.');
    }
  };

  useEffect(() => {
    loadMessages();
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    loadOverallStats();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      new: { label: 'Nou', className: 'bg-green-100 text-green-800' },
      read: { label: 'Citit', className: 'bg-blue-100 text-blue-800' },
      replied: { label: 'Notite', className: 'bg-purple-100 text-purple-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const stats = {
    total: overallStats.total,
    new: overallStats.byStatus.new,
    read: overallStats.byStatus.read,
    replied: overallStats.byStatus.replied
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Mesaje de contact</h1>
          <p className="text-gray-600">Gestionează mesajele primite prin formularul de contact</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Noi</p>
                <p className="text-xl font-bold text-gray-900">{stats.new}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Citite</p>
                <p className="text-xl font-bold text-gray-900">{stats.read}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Notite</p>
                <p className="text-xl font-bold text-gray-900">{stats.replied}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Caută în mesaje..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Filtrează:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Toate</option>
                <option value="new">Noi</option>
                <option value="read">Citite</option>
                <option value="replied">Notite</option>
              </select>
            </div>
          </div>
        </div>

        {/* Email-like Interface */}
        <div className="bg-white shadow rounded-lg" style={{ height: '600px' }}>
          <div className="flex h-full">
            {/* Left Panel - Messages List */}
            <div className={`w-full md:w-1/2 md:border-r border-gray-200 ${showMobileDetails ? 'hidden md:block' : ''}`}>
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Inbox ({messages.length})</h3>
              </div>
              <div className="overflow-y-auto" style={{ height: 'calc(100% - 65px)' }}>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-red-600">{error}</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">Nu există mesaje</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedMessage?.id === message.id ? 'bg-blue-50 border-r-4 border-blue-500' : ''
                          } ${message.status === 'new' ? 'bg-green-50' : ''}`}
                        onClick={() => selectMessage(message)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`font-medium ${message.status === 'new' ? 'text-green-800' : 'text-gray-900'}`}>
                              {message.name}
                            </span>
                            {getStatusBadge(message.status)}
                            {message.attachment_name && (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.createdAt)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">{message.email}</div>
                        <div className="text-sm font-medium text-gray-900 mb-1">{message.subject}</div>
                        <div className="text-sm text-gray-600 truncate">{message.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Message Details */}
            <div className={`w-full md:w-1/2 ${!showMobileDetails ? 'hidden md:block' : ''}`}>
              {selectedMessage ? (
                <div className="h-full flex flex-col">
                  {/* Message Header */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {/* Back button for mobile */}
                        <button
                          onClick={backToList}
                          className="md:hidden text-gray-600 hover:text-gray-800"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{selectedMessage.subject}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-600">De la: {selectedMessage.name}</span>
                            <span className="text-sm text-gray-600 hidden sm:inline">{selectedMessage.email}</span>
                            {selectedMessage.phone && (
                              <span className="text-sm text-gray-600 hidden sm:inline">{selectedMessage.phone}</span>
                            )}
                          </div>
                          {/* Show email and phone on mobile below */}
                          <div className="sm:hidden mt-1">
                            <div className="text-sm text-gray-600">{selectedMessage.email}</div>
                            {selectedMessage.phone && (
                              <div className="text-sm text-gray-600">{selectedMessage.phone}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(selectedMessage.status)}
                        <button
                          onClick={() => handleDelete(selectedMessage.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      Primit la: {formatDate(selectedMessage.createdAt)}
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className="flex-1 p-4 overflow-y-auto">
                    <div className="prose max-w-none">
                      <p className="whitespace-pre-wrap text-gray-900">{selectedMessage.message}</p>
                    </div>

                    {/* Attachment Section */}
                    {selectedMessage.attachment_name && (
                      <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{selectedMessage.attachment_name}</span>
                          </div>
                          <Button
                            onClick={(e) => {
                              if (selectedMessage.attachment_name) {
                                handleDownloadAttachment(e, selectedMessage.id, selectedMessage.attachment_name);
                              }
                            }}
                            variant="outline"
                            className="flex items-center space-x-2 text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span>Descarcă</span>
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Existing Notes */}
                    {selectedMessage.reply && (
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Notele tale:</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.reply}</p>
                        <div className="text-xs text-gray-500 mt-2">
                          Adăugat la: {selectedMessage.repliedAt && formatDate(selectedMessage.repliedAt)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Note Form */}
                  <div className="p-4 border-t border-gray-200">
                    {selectedMessage.status !== 'replied' && (
                      <>
                        {!showReplyForm ? (
                          <Button
                            onClick={() => setShowReplyForm(true)}
                            className="bg-primary text-white hover:bg-primary-dark"
                          >
                            Adaugă notă
                          </Button>
                        ) : (
                          <div className="space-y-3">
                            <textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Scrie nota aici..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              rows={4}
                            />
                            <div className="flex space-x-2">
                              <Button
                                onClick={handleReply}
                                disabled={isReplying || !replyText.trim()}
                                className="bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
                              >
                                {isReplying ? 'Se salvează...' : 'Salvează nota'}
                              </Button>
                              <Button
                                onClick={() => {
                                  setShowReplyForm(false);
                                  setReplyText('');
                                }}
                                variant="outline"
                              >
                                Anulează
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center px-4">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm md:text-base">Selectează un mesaj pentru a-l vizualiza</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact; 